// src/stores/auth.js
import { defineStore } from 'pinia'
import { auth } from '@/firebase/init'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import type { User } from 'firebase/auth'
import router from '@/router'
import { useUserStore } from './userStore' // adjust the path if needed
import { useInviteStore } from '@/stores/inviteStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useClosedDayStore } from '@/stores/closedDayStore'
import { useTextListStore } from '@/stores/textListStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useOrderStore } from '@/stores/orderStore'
import { useAnalysisResultStore } from '@/stores/analysisResultStore'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useLabEquipmentStore } from '@/stores/labEquipmentStore'
import { useDhlShipmentStore } from '@/stores/dhlShipmentStore'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as User | null, // Firebase user or null
    ready: false, // becomes true after first onAuthStateChanged fires
  }),

  actions: {
    init(): Promise<void> {
      // avoid attaching multiple listeners
      if (this.ready) return Promise.resolve()

      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          this.user = user
          this.ready = true

          if (user) {
            const userStore = useUserStore()
            userStore.getCurrentUser(user.uid)
          }

          resolve() // let main.js continue
          unsubscribe() // optional: if you don't need realtime updates
          // If you want to stay synced to auth changes, remove unsubscribe()
        })
      })
    },

    updateUser(user: User | null) {
      this.user = user
    },

    async logout({
      reason,
      urlSlug,
    }: { reason?: string; urlSlug?: string } = {}): Promise<void> {
      const appStore = useAppStore()
      const userStore = useUserStore()
      const inviteStore = useInviteStore()
      const accountStore = useAccountStore()
      const analysisTestStore = useAnalysisTestStore()
      const analysisStore = useAnalysisStore()
      const turnaroundTimeStore = useTurnaroundTimeStore()
      const textListStore = useTextListStore()
      const pricingSegmentStore = usePricingSegmentStore()
      const customerAccountStore = useCustomerAccountStore()
      const insuranceCustomerAccountStore = useInsuranceCustomerAccountStore()
      const orderStore = useOrderStore()
      const dropPointStore = useDropPointStore()
      const customerUserStore = useCustomerUserStore()
      const analysisResultStore = useAnalysisResultStore()
      const reportTemplateStore = useReportTemplateStore()
      const reportDescriptionStore = useReportDescriptionStore()
      const reportDescriptionItemStore = useReportDescriptionItemStore()
      const sampleAreaStore = useSampleAreaStore()

      const labSlug = accountStore.labAccount?.subDomain

      // Unsubscribe all Firestore listeners before signing out — signing out first
      // invalidates the auth token while listeners are still open, which causes
      // Firestore to raise uncaught permission-denied errors on those listeners.
      userStore.resetStore()
      inviteStore.resetStore()
      analysisTestStore.resetStore()
      analysisStore.resetStore()
      turnaroundTimeStore.resetStore()
      useClosedDayStore().resetStore()
      textListStore.resetStore()
      pricingSegmentStore.resetStore()
      customerAccountStore.resetStore()
      insuranceCustomerAccountStore.resetStore()
      orderStore.resetStore()
      dropPointStore.resetStore()
      useDhlShipmentStore().resetStore()
      customerUserStore.resetStore()
      analysisResultStore.resetStore()
      reportTemplateStore.resetStore()
      reportDescriptionStore.resetStore()
      reportDescriptionItemStore.resetStore()
      sampleAreaStore.resetStore()
      useLabEquipmentStore().resetStore()
      accountStore.resetStore()

      await signOut(auth)

      this.user = null

      if (reason === 'deactivated' || reason === 'no-profile') {
        if (!urlSlug || urlSlug === appStore.defaultLabSlug) {
          const doReload = router.currentRoute.value.name === 'Home'
          await router.push({
            name: 'Home',
            query: { reason },
          })

          // Reload is needed when already on login page to trigger the deactivation message
          if (doReload) {
            window.location.reload()
          }
        } else {
          const doReload = router.currentRoute.value.name === 'LabHome'
          await router.push({
            name: 'LabHome',
            params: {
              labSlug: urlSlug,
            },
            query: { reason },
          })

          // Reload is needed when already on login page to trigger the deactivation message
          if (doReload) {
            window.location.reload()
          }
        }
        return
      }

      await router.push({
        name: 'LabHome',
        params: {
          labSlug: labSlug ?? appStore.defaultLabSlug,
        },
      })
    },
  },

  getters: {
    isLoggedIn: (state) => !!state.user,
  },
})

